<script setup lang="ts">
import { nextTick, onMounted, ref, type PropType } from 'vue';
import { type FormField, type MatrixColumn, type NumberRange } from '@/models';
import InputNumber from 'primevue/inputnumber';

const props = defineProps({
  modelValue: {
    type: String
  },
  range: {
    type: Object as PropType<NumberRange>,
    required: true
  },
  field: {
    type: Object as PropType<FormField>
  },
  column: {
    type: Object as PropType<MatrixColumn>
  },
  previewMode: {
    type: Boolean
  }
});

const localField = ref<FormField | undefined>(props.field);
const localColumn = ref<MatrixColumn | undefined>(props.column);
const localValue = ref<string | undefined>(props.modelValue);
const emit = defineEmits(['update:modelValue']);

onMounted(() => {
  localValue.value = props.modelValue;
  if (localValue.value) {
    const [start, end] = localValue.value.split('-').map(Number);
    range1.value = start;
    range2.value = end;
  }
});

const range1 = ref<number | string>();
const range2 = ref<number | string>();

const onRange1Blur = () => {
  nextTick(() => {
    if (!range2.value) {
      range2.value = range1.value;
    }

    if (range2.value && range1.value && range2.value < range1.value) {
      range2.value = undefined;
      nextTick(() => {
        range2.value = range1.value;
      });
    }

    nextTick(() => {
      setFieldValue();
    });
  });
};

const onRange2Blur = () => {
  nextTick(() => {
    if (
      range1.value &&
      range2.value &&
      range2.value < range1.value
    ) {
      range2.value = undefined;
      nextTick(() => {
        range2.value = range1.value;
      });
    }

    if (!range2.value) {
      nextTick(() => {
        range2.value = range1.value;
      });
    }

    nextTick(() => {
      setFieldValue();
    });
  });
};

const setFieldValue = () => {
  // changing this because !range1.value is false for 0
  if (range1.value !== undefined && range2.value !== undefined && range1.value !== null && range2.value !== null) {
    localValue.value = `${range1.value}-${range2.value}`;
  } else {
    localValue.value = '';
  }

  emit('update:modelValue', localValue.value);

  if (!isRangeWithinAllowed()) {
    if (localField.value) {
      localField.value.warn = 'Value out of allowed range; please double-check';
    }
    if (localColumn.value) {
      localColumn.value.warn = 'Value out of allowed range; please double-check';
    }
  } else {
    if (localField.value) {
      delete localField.value.warn;
    }
    if (localColumn.value) {
      delete localColumn.value.warn;
    }
  }
};

const isRangeWithinAllowed = () => {
  const range = props.range!;
  // changing this because !range1.value is false for 0
  if (range1.value === undefined || range2.value === undefined || range1.value === null || range2.value === null) {
    // if any are empty or range undefined, consider valid or adjust as needed
    return false;
  }
  return (
    parseFloat(`${range1.value}`) >= range.rangeStart &&
    parseFloat(`${range2.value}`) <= range.rangeEnd &&
    range1.value <= range2.value
  );
};
</script>

<template>
  <div class="item-root">
    <InputNumber id="range1"
        v-model="range1"
      :disabled="previewMode"
      mode="decimal"
      :maxFractionDigits="20"
      @blur="onRange1Blur"/>

    <div class="dash">-</div>

    <InputNumber id="range2"
      v-model="range2"
      :disabled="previewMode"
      mode="decimal"
      :maxFractionDigits="20"
        @blur="onRange2Blur"/>
  </div>
</template>

<style scoped lang="scss">
.item-root {
  display: flex;
  align-items: center;
  gap: 8px;
  .text-input {
    min-height: 38px;
  }
  :deep(.p-inputnumber-input) {
    max-width: 100px;
  }
  .dash {
    font-size: 20px;
    font-weight: bold;
    color: var(--text-color);
  }
}
</style>
